import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Share2, Heart, MessageCircle, Repeat2, PieChart, Trophy, TrendingUp } from 'lucide-react';
import { dashboardAPI } from '../../../services/dashboardService';
import StatCard from './ui/StatCard';
import SectionCard from './ui/SectionCard';
import ProgressBar from './ui/ProgressBar';
import DataTable from './ui/DataTable';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';

const SocialTab = ({ filters, onViewDetailed }) => {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const POST_TYPE_CATEGORIES = useMemo(() => [
        { value: 'post', label: t('systemDashboard.social.post'), color: 'bg-blue-500' },
        { value: 'poll', label: t('systemDashboard.social.poll'), color: 'bg-violet-500' },
        { value: 'adoption', label: t('systemDashboard.social.adoption'), color: 'bg-pink-500' }
    ], [t]);

    const POST_TYPE_LABELS = {
        post: t('systemDashboard.social.post'),
        poll: t('systemDashboard.social.poll'),
        adoption: t('systemDashboard.social.adoption')
    };

    const TOP_POST_COLUMNS = [
        {
            key: 'content',
            header: t('systemDashboard.social.columns.content'),
            render: (row) => (
                <p className="sd-post-content" title={row.content}>
                    {row.content || t('systemDashboard.social.noContent')}
                </p>
            )
        },
        { key: 'author', header: t('systemDashboard.social.columns.author') },
        {
            key: 'type',
            header: t('systemDashboard.social.columns.type'),
            width: '120px',
            render: (row) => (
                <span className="sd-post-type-badge">
                    {POST_TYPE_LABELS[row.type] || row.type || t('systemDashboard.social.post')}
                </span>
            )
        },
        {
            key: 'engagementCount',
            header: t('systemDashboard.social.columns.engagement'),
            width: '120px',
            render: (row) => (
                <span className="sd-post-engagement">{(row.engagementCount || 0).toLocaleString()}</span>
            )
        }
    ];

    const loadSocialStats = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await dashboardAPI.getSocialStats(filters);
            setStats(response.data);
        } catch (err) {
            console.error('Failed to load social stats:', err);
            setError(err.message || t('systemDashboard.social.loadError'));
        } finally {
            setLoading(false);
        }
    }, [filters, t]);

    useEffect(() => {
        loadSocialStats();
    }, [loadSocialStats]);

    const postTypeData = useMemo(() => {
        return POST_TYPE_CATEGORIES.map((category) => ({
            name: category.label,
            value: stats?.postsByType?.[category.value] || 0
        }));
    }, [POST_TYPE_CATEGORIES, stats?.postsByType]);

    const graphData = stats?.graph_data || [];

    const engagementItems = [
        { icon: Heart, label: t('systemDashboard.social.likes'), value: stats?.likes || 0, color: 'text-rose-500', bg: 'bg-rose-50' },
        { icon: MessageCircle, label: t('systemDashboard.social.comments'), value: stats?.comments || 0, color: 'text-blue-500', bg: 'bg-blue-50' },
        { icon: Repeat2, label: t('systemDashboard.social.shares'), value: stats?.shares || 0, color: 'text-emerald-500', bg: 'bg-emerald-50' }
    ];

    if (error) {
        return (
            <div className="error-container">
                <p>{error}</p>
                <button onClick={loadSocialStats} className="retry-button">{t('systemDashboard.common.retry')}</button>
            </div>
        );
    }

    return (
        <div className="sd-container">
            <div className="sd-detailed-link-row">
                <button
                    onClick={onViewDetailed}
                    className="sd-detailed-link"
                >
                    {t('systemDashboard.common.detailed')} <ArrowRight size={14} />
                </button>
            </div>

            {/* KPI Cards */}
            <div className="sd-grid sd-grid--kpi-primary">
                <StatCard
                    icon={Share2}
                    label={t('systemDashboard.social.totalPosts')}
                    value={stats?.totalPosts || 0}
                    trend={stats?.postGrowth}
                    trendLabel=""
                    color="bg-violet-500"
                    loading={loading}
                />
                <StatCard
                    icon={Heart}
                    label={t('systemDashboard.social.engagement')}
                    value={stats?.totalEngagement || 0}
                    subtitle={t('systemDashboard.social.engagementSubtitle')}
                    color="bg-pink-500"
                    loading={loading}
                />
                <StatCard
                    icon={TrendingUp}
                    label={t('systemDashboard.social.avgRate')}
                    value={`${(stats?.avgEngagementRate || 0).toFixed(1)}%`}
                    subtitle={t('systemDashboard.social.avgRateSubtitle')}
                    color="bg-blue-500"
                    loading={loading}
                />
                <StatCard
                    icon={Repeat2}
                    label={t('systemDashboard.social.shares')}
                    value={stats?.shares || 0}
                    color="bg-emerald-500"
                    loading={loading}
                />
            </div>

            {/* Charts */}
            {!loading && (
                <div className="sd-grid sd-grid--charts">
                    <SectionCard title={t('systemDashboard.social.contentDistribution')} icon={PieChart}>
                        <CustomDonutChart
                            data={postTypeData}
                            totalValue={stats?.totalPosts || 0}
                            title=""
                        />
                    </SectionCard>
                    <SectionCard title={t('systemDashboard.social.postActivity')} icon={TrendingUp} className="sd-card--wide">
                        <CustomAreaChart data={graphData} color="#ec4899" title="" />
                    </SectionCard>
                </div>
            )}

            {/* Engagement Breakdown + Post Type Distribution */}
            {!loading && (
                <div className="sd-grid sd-grid--split">
                    <SectionCard title={t('systemDashboard.social.engagementBreakdown')} icon={Heart}>
                        <div>
                            {engagementItems.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <div
                                        key={item.label}
                                        className="sd-engagement-item"
                                    >
                                        <div className="sd-engagement-item__left">
                                            <div className={`sd-engagement-item__icon ${item.bg} ${item.color}`}>
                                                <Icon size={15} />
                                            </div>
                                            <span className="sd-engagement-item__label">{item.label}</span>
                                        </div>
                                        <span className="sd-engagement-item__value">
                                            {item.value.toLocaleString()}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </SectionCard>

                    <SectionCard title={t('systemDashboard.social.postTypeDistribution')} icon={PieChart}>
                        <div className="sd-progress-bar-list">
                            {POST_TYPE_CATEGORIES.map((category) => {
                                const count = stats?.postsByType?.[category.value] || 0;
                                return (
                                    <ProgressBar
                                        key={category.value}
                                        label={category.label}
                                        count={count}
                                        total={stats?.totalPosts || 1}
                                        color={category.color}
                                    />
                                );
                            })}
                        </div>
                    </SectionCard>
                </div>
            )}

            {/* Top Posts */}
            {!loading && (
                <SectionCard title={t('systemDashboard.social.topPosts')} icon={Trophy}>
                    <DataTable
                        columns={TOP_POST_COLUMNS}
                        data={stats?.topPosts?.slice(0, 5) || []}
                        emptyText={t('systemDashboard.social.noTopPosts')}
                    />
                </SectionCard>
            )}
        </div>
    );
};

export default SocialTab;
